import {
    Box,
    Button,
    Center,
    PasswordInput,
    Stack,
    Text,
    TextInput,
} from "@mantine/core"
import { Document, useMedplum } from "@medplum/react"

import { MfaInput } from "../components/auth/MfaInput"
import { TenantSelection } from "../components/auth/TenantSelection"

import React, { JSX, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { generatePKCE, getStoredPKCE, storePKCE } from "../components/auth/pkce"
import { startLogin } from "../hooks/ovokApi"
import TelmedaLogo from "./ui/TelmedaLogo"

export function SignInPage(): JSX.Element {
    const navigate = useNavigate()
    const medplum = useMedplum()

    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [loginState, setLoginState] = useState<"initial" | "mfa" | "tenant">(
        "initial"
    )
    const [loginData, setLoginData] = useState<{
        loginId?: string
        sessionCode?: string
        profiles?: Array<{
            tenantCode: string
            project: {
                id: string
                resourceType: string
                name: string
            }
            profile: {
                resourceType: string
                id: string
                meta: {
                    project: string
                    versionId: string
                    lastUpdated: string
                    author: { reference: string }
                    compartment: Array<{ reference: string }>
                }
                name: Array<{ given: string[]; family: string }>
                telecom: Array<{ system: string; use: string; value: string }>
            }
        }>
    }>({})

    useEffect(() => {
        // Generate and store PKCE values when component mounts
        const generateAndStorePKCE = async () => {
            const pkce = await generatePKCE()
            storePKCE(pkce)
        }
        generateAndStorePKCE()

        getEmailFromUrl();
    }, [])


    function getEmailFromUrl() {
        const urlParams = new URLSearchParams(window.location.search)
        const email = urlParams.get("email")

        if (email) {
            setEmail(email)
        }
    }

    const onEmailSubmit = async () => {
        try {
            const pkce = getStoredPKCE()
            if (!pkce) {
                setErrorMessage(
                    "PKCE values not found. Please refresh the page."
                )
                return
            }

            const response = await startLogin(
                email,
                password,
                pkce.codeChallenge
            )

            if (response.nextStep === "mfa") {
                setLoginState("mfa")
                setLoginData({ loginId: response.loginId })
            } else if (response.nextStep === "token" && response.profiles) {
                setLoginState("tenant")
                setLoginData({
                    sessionCode: response.sessionCode,
                    profiles: response.profiles,
                })
            }
        } catch (error) {
            setErrorMessage(
                error instanceof Error
                    ? error.message
                    : "An unexpected error occurred"
            )
        }
    }

    const handleMfaSuccess = (response: {
        sessionCode: string
        profiles: Array<{
            tenantCode: string
            project: {
                id: string
                resourceType: string
                name: string
            }
            profile: {
                resourceType: string
                id: string
                meta: {
                    project: string
                    versionId: string
                    lastUpdated: string
                    author: { reference: string }
                    compartment: Array<{ reference: string }>
                }
                name: Array<{ given: string[]; family: string }>
                telecom: Array<{ system: string; use: string; value: string }>
            }
        }>
    }) => {
        setLoginState("tenant")
        setLoginData({
            sessionCode: response.sessionCode,
            profiles: response.profiles,
        })
    }

    const handleTenantSuccess = async (response: {
        accessToken: string
        refreshToken: string
        project: { reference: string; display: string }
        profile: { reference: string; display: string }
        expiresIn: number
    }) => {
        try {
            await medplum.setActiveLogin({
                accessToken: response.accessToken,
                refreshToken: response.refreshToken,
                project: response.project,
                profile: response.profile,
            })
            navigate("/")
        } catch (error) {
            setErrorMessage(
                error instanceof Error
                    ? error.message
                    : "Failed to set active login. Please try again."
            )
        }
    }

    return (
        <Document style={{ width: "60%" }}>
            <Center style={{ flexDirection: "column" }}>
                <Box mb="lg">
                    <TelmedaLogo width={100} full={true} />
                </Box>

                <Text mb="md" c="#7F56D9" fz={16}>
                    Practitioner Sign In
                </Text>

                {errorMessage && (
                    <Text
                        w="100%"
                        mb="md"
                        c="white"
                        bg="red"
                        ta="center"
                        py={10}
                        px={20}
                        sx={{ borderRadius: "4px" }}
                    >
                        {errorMessage}
                    </Text>
                )}

                {loginState === "initial" && (
                    <Stack w="100%" gap="xs">
                        <TextInput
                            w="100%"
                            name="email"
                            type="email"
                            placeholder="email"
                            required={true}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />

                        <PasswordInput
                            w="100%"
                            name="password"
                            placeholder="password"
                            autoComplete="off"
                            required={true}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />

                        <Button
                            mt="xl"
                            type="submit"
                            disabled={!email || !password}
                            onClick={onEmailSubmit}
                        >
                            Sign In
                        </Button>
                    </Stack>
                )}

                {loginState === "mfa" && loginData.loginId && (
                    <MfaInput
                        loginId={loginData.loginId}
                        onSuccess={handleMfaSuccess}
                        onError={setErrorMessage}
                    />
                )}

                {loginState === "tenant" &&
                    loginData.sessionCode &&
                    loginData.profiles && (
                        <TenantSelection
                            sessionCode={loginData.sessionCode}
                            codeVerifier={getStoredPKCE()?.codeVerifier || ""}
                            profiles={loginData.profiles}
                            onSuccess={handleTenantSuccess}
                            onError={setErrorMessage}
                        />
                    )}
            </Center>
        </Document>
    )
}
